import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Pagination, Tooltip
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { fetchData, postData, putData, deleteData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PAGE_SIZE = 10;

// Step indicator
function StepIndicator({ step }) {
  const steps = ["Logos", "Descriptions"];
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
      {steps.map((label, i) => {
        const num = i + 1;
        const active = num === step;
        const done = num < step;
        return (
          <Box key={num} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: "50%",
              background: active || done ? "linear-gradient(90deg, #4A7BA8, #244C77)" : "#E5E7EB",
              color: active || done ? "#fff" : "#6B7280",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "13px", fontWeight: 700,
            }}>
              {num}
            </Box>
            <Typography sx={{ fontSize: "13px", fontWeight: active ? 700 : 400, color: active ? "#244C77" : "#6B7280" }}>
              {label}
            </Typography>
            {i < steps.length - 1 && (
              <Box sx={{ width: 32, height: 2, backgroundColor: done ? "#244C77" : "#E5E7EB", mx: 0.5 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function ConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "#374151" }}>Are you sure you want to delete this item?</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: "8px" }}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

const btnStyle = {
  background: "linear-gradient(90deg, #4A7BA8, #244C77)",
  color: "#fff", fontSize: "13px", textTransform: "none",
  borderRadius: "10px", px: 2.5, py: 1,
  "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
};

const navBtnStyle = {
  background: "linear-gradient(90deg, #4A7BA8, #244C77)",
  color: "#fff", fontSize: "13px", textTransform: "none",
  borderRadius: "10px", px: 3, py: 1,
  "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
};

const LOGOS_FALLBACK = [
  { _id: "1", orgName: "GA DOE Seal", imageUrl: "" },
  { _id: "2", orgName: "Avant STAMP WS", imageUrl: "" },
  { _id: "3", orgName: "ALTA Logo", imageUrl: "" },
];

const DESCRIPTIONS_FALLBACK = [
  { _id: "1", title: "What is Seal of Biliteracy?", description: "The Seal of Biliteracy is an award given by a school, district, or state in recognition of students who have studied and attained proficiency in two or more languages by high school graduation." },
  { _id: "2", title: "Benefits of Biliteracy", description: "Earning the Seal of Biliteracy demonstrates readiness for college and careers in our multilingual world, strengthens resumes, and acknowledges the value of language diversity in our global society." },
];

/* ─────────────────────────── STEP 1: Logos ─────────────────────────── */
function Step1Logos({ onNext }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/seal-content/list?type=logo");
      setRows(Array.isArray(res?.data) ? res.data : LOGOS_FALLBACK);
    } catch { setRows(LOGOS_FALLBACK); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setOrgName(""); setImageFile(null); setImagePreview(""); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setOrgName(row.orgName || "");
    setImagePreview(row.imageUrl || "");
    setImageFile(null);
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setEditId(null); setOrgName(""); setImageFile(null); setImagePreview(""); };

  const handleSave = async () => {
    const fd = new FormData();
    fd.append("orgName", orgName);
    fd.append("type", "logo");
    if (imageFile) fd.append("image", imageFile);
    try {
      if (editId) { await putData(`admin/seal-content/update/${editId}`, fd); }
      else { await postData("admin/seal-content/create", fd); }
      setToast({ open: true, severity: "success", message: editId ? "Logo updated." : "Logo added." });
      closeDialog(); load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to save." }); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/seal-content/${id}`);
      setToast({ open: true, severity: "success", message: "Logo deleted." });
      load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to delete." }); }
  };

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button startIcon={<AddIcon />} onClick={openAdd} sx={btnStyle}>+ Add Logo</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Image", "Organization Name", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No logos yet.</TableCell></TableRow>
              ) : pageData.map((row, idx) => (
                <TableRow key={row._id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <TableCell sx={{ fontSize: "13px", width: 60 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                  <TableCell>
                    {row.imageUrl ? (
                      <img src={row.imageUrl} alt={row.orgName} style={{ width: 60, height: 60, objectFit: "contain", borderRadius: "8px", border: "1px solid #E5E7EB" }} />
                    ) : (
                      <Box sx={{ width: 60, height: 60, borderRadius: "8px", backgroundColor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #D1D5DB" }}>
                        <AddPhotoAlternateIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{row.orgName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77" }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button onClick={onNext} sx={navBtnStyle}>Next →</Button>
      </Box>

      {/* Logo Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>{editId ? "Edit Logo" : "Add Logo"}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Box component="label" sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: "2px dashed #D1D5DB", borderRadius: "10px", height: 160, cursor: "pointer",
            backgroundColor: "#F9FAFB", "&:hover": { borderColor: "#4A7BA8" }, overflow: "hidden", mb: 2,
          }}>
            {imagePreview ? (
              <img src={imagePreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <>
                <AddPhotoAlternateIcon sx={{ fontSize: "40px", color: "#9CA3AF", mb: 1 }} />
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Click to upload logo</Typography>
              </>
            )}
            <input type="file" hidden accept=".jpg,.jpeg,.png,.webp,.svg" onChange={e => {
              const f = e.target.files[0]; if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
            }} />
          </Box>
          <TextField label="Organization Name" fullWidth size="small" value={orgName}
            onChange={e => setOrgName(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>{editId ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── STEP 2: Descriptions ─────────────────────────── */
function Step2Descriptions({ onBack }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/seal-content/list?type=description");
      setRows(Array.isArray(res?.data) ? res.data : DESCRIPTIONS_FALLBACK);
    } catch { setRows(DESCRIPTIONS_FALLBACK); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm({ title: "", description: "" }); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setForm({ title: row.title || "", description: row.description || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, type: "description" };
      if (editId) { await putData(`admin/seal-content/update/${editId}`, payload); }
      else { await postData("admin/seal-content/create", payload); }
      setToast({ open: true, severity: "success", message: editId ? "Description updated." : "Description added." });
      setDialogOpen(false); load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to save." }); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/seal-content/${id}`);
      setToast({ open: true, severity: "success", message: "Deleted." });
      load();
    } catch { setToast({ open: true, severity: "error", message: "Failed to delete." }); }
  };

  const handleUpdate = () => setToast({ open: true, severity: "success", message: "Seal of Biliteracy configuration saved." });

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button startIcon={<AddIcon />} onClick={openAdd} sx={btnStyle}>+ Add Description</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Title", "Description", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No descriptions yet.</TableCell></TableRow>
              ) : pageData.map((row, idx) => (
                <TableRow key={row._id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <TableCell sx={{ fontSize: "13px", width: 60 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827", maxWidth: 200 }}>
                    <Typography noWrap sx={{ fontSize: "13px", maxWidth: 180 }}>{row.title}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 350 }}>
                    <Typography noWrap sx={{ fontSize: "13px", color: "#6B7280", maxWidth: 330 }}>{row.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77" }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button onClick={onBack} sx={{ ...navBtnStyle, background: "#F3F4F6", color: "#374151", "&:hover": { background: "#E5E7EB" } }}>← Back</Button>
        <Button startIcon={<SaveOutlinedIcon />} onClick={handleUpdate} sx={navBtnStyle}>Update</Button>
      </Box>

      {/* Description Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px" }}>{editId ? "Edit Description" : "Add Description"}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField label="Title" fullWidth size="small" value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Description" fullWidth size="small" multiline rows={5} value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>{editId ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ─────────────────────────── MAIN COMPONENT ─────────────────────────── */
const AdminSealBiliteracyConfig = () => {
  const [step, setStep] = useState(1);

  const stepTitles = {
    1: { title: "Seal of Biliteracy — Logos", sub: "Manage organization logos for Seal of Biliteracy" },
    2: { title: "Seal of Biliteracy — Descriptions", sub: "Manage descriptive content for Seal of Biliteracy" },
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Seal Of Biliteracy / Seal Of Biliteracy displayed in the website
      </p>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <WorkspacePremiumIcon sx={{ color: "#244C77", fontSize: "28px" }} />
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>
            {stepTitles[step].title}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>{stepTitles[step].sub}</Typography>
        </Box>
      </Box>

      <StepIndicator step={step} />

      {step === 1 && <Step1Logos onNext={() => setStep(2)} />}
      {step === 2 && <Step2Descriptions onBack={() => setStep(1)} />}
    </div>
  );
};

export default AdminSealBiliteracyConfig;
